import { TaskTemplateDraft } from '../../types';
import { State, Action } from './types';

function moveByIndex<T extends { id: string }>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number
) {
  const next = [...items];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(destinationIndex, 0, moved);
  return next;
}

function syncInverseDependencies(
  templates: TaskTemplateDraft[],
  sourceId: string,
  addedSuccessors: string[],
  removedSuccessors: string[],
  addedPredecessors: string[],
  removedPredecessors: string[]
): TaskTemplateDraft[] {
  return templates.map((template) => {
    if (template.id === sourceId) return template;

    let { predecessors, successors } = template.dependencies;

    // Source was added as a "blocks" target → template is now source's successor
    // → template gains source as a predecessor
    if (
      addedSuccessors.includes(template.id) &&
      !predecessors.includes(sourceId)
    ) {
      predecessors = [...predecessors, sourceId];
    }
    // Source removed template from its successors → remove source from template's predecessors
    if (removedSuccessors.includes(template.id)) {
      predecessors = predecessors.filter((id) => id !== sourceId);
    }

    // Source was added as a "depends on" target → template is now source's predecessor
    // → template gains source as a successor
    if (
      addedPredecessors.includes(template.id) &&
      !successors.includes(sourceId)
    ) {
      successors = [...successors, sourceId];
    }
    // Source removed template from its predecessors → remove source from template's successors
    if (removedPredecessors.includes(template.id)) {
      successors = successors.filter((id) => id !== sourceId);
    }

    if (
      predecessors === template.dependencies.predecessors &&
      successors === template.dependencies.successors
    ) {
      return template;
    }

    return { ...template, dependencies: { predecessors, successors } };
  });
}

function removeTemplateReferences(
  templates: TaskTemplateDraft[],
  removedTemplateId: string
): TaskTemplateDraft[] {
  return templates.map((template) => ({
    ...template,
    dependencies: {
      predecessors: template.dependencies.predecessors.filter(
        (id) => id !== removedTemplateId
      ),
      successors: template.dependencies.successors.filter(
        (id) => id !== removedTemplateId
      ),
    },
  }));
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'update_bundle_field':
      return {
        ...state,
        draft: {
          ...state.draft,
          bundle: {
            ...state.draft.bundle,
            [action.field]: action.value,
          },
        },
      };

    case 'add_template': {
      const nextModified = new Set(state.modifiedTemplateIds);
      nextModified.add(action.template.id);

      const withNewTemplate = [...state.draft.templates, action.template];
      const { predecessors, successors } = action.template.dependencies;

      const syncedTemplates = syncInverseDependencies(
        withNewTemplate,
        action.template.id,
        successors,
        [],
        predecessors,
        []
      );

      return {
        ...state,
        draft: {
          ...state.draft,
          templates: syncedTemplates,
        },
        modifiedTemplateIds: nextModified,
      };
    }

    case 'update_template': {
      const nextModified = new Set(state.modifiedTemplateIds);
      nextModified.add(action.templateId);

      // Strip self-references from the dependency patch before applying so the
      // reducer is the source of truth for valid state shape (the UI also
      // filters via getAvailableTasks, but the reducer should be authoritative).
      const sanitisedPatch =
        action.patch.dependencies != null
          ? {
              ...action.patch,
              dependencies: {
                predecessors: action.patch.dependencies.predecessors.filter(
                  (id) => id !== action.templateId
                ),
                successors: action.patch.dependencies.successors.filter(
                  (id) => id !== action.templateId
                ),
              },
            }
          : action.patch;

      const patchedTemplates = state.draft.templates.map((template) =>
        template.id === action.templateId
          ? { ...template, ...sanitisedPatch }
          : template
      );

      if (!sanitisedPatch.dependencies) {
        return {
          ...state,
          draft: {
            ...state.draft,
            templates: patchedTemplates,
          },
          modifiedTemplateIds: nextModified,
        };
      }

      const oldDeps = state.draft.templates.find(
        (t) => t.id === action.templateId
      )?.dependencies ?? { predecessors: [], successors: [] };
      const newDeps = sanitisedPatch.dependencies!;

      const addedSuccessors = newDeps.successors.filter(
        (id) => !oldDeps.successors.includes(id)
      );
      const removedSuccessors = oldDeps.successors.filter(
        (id) => !newDeps.successors.includes(id)
      );
      const addedPredecessors = newDeps.predecessors.filter(
        (id) => !oldDeps.predecessors.includes(id)
      );
      const removedPredecessors = oldDeps.predecessors.filter(
        (id) => !newDeps.predecessors.includes(id)
      );

      const syncedTemplates = syncInverseDependencies(
        patchedTemplates,
        action.templateId,
        addedSuccessors,
        removedSuccessors,
        addedPredecessors,
        removedPredecessors
      );

      return {
        ...state,
        draft: {
          ...state.draft,
          templates: syncedTemplates,
        },
        modifiedTemplateIds: nextModified,
      };
    }

    case 'remove_template': {
      const nextModified = new Set(state.modifiedTemplateIds);
      nextModified.delete(action.templateId);

      const remainingTemplates = state.draft.templates.filter(
        (template) => template.id !== action.templateId
      );

      return {
        ...state,
        draft: {
          ...state.draft,
          templates: removeTemplateReferences(
            remainingTemplates,
            action.templateId
          ),
        },
        modifiedTemplateIds: nextModified,
      };
    }

    case 'reorder_templates': {
      const reordered = moveByIndex(
        state.draft.templates,
        action.sourceIndex,
        action.destinationIndex
      );

      const nextModified = new Set(state.modifiedTemplateIds);
      const from = Math.min(action.sourceIndex, action.destinationIndex);
      const to = Math.max(action.sourceIndex, action.destinationIndex);
      const templatesToUpdate = reordered.slice(from, to + 1);
      templatesToUpdate.forEach((template) => {
        if (template.id) {
          nextModified.add(template.id);
        }
      });

      return {
        ...state,
        draft: {
          ...state.draft,
          templates: reordered,
        },
        modifiedTemplateIds: nextModified,
      };
    }

    case 'replace_template_dependencies': {
      const nextModified = new Set(state.modifiedTemplateIds);
      nextModified.add(action.templateId);

      const patchedTemplates = state.draft.templates.map((template) =>
        template.id === action.templateId
          ? { ...template, dependencies: action.dependencies }
          : template
      );

      const oldDeps = state.draft.templates.find(
        (t) => t.id === action.templateId
      )?.dependencies ?? { predecessors: [], successors: [] };
      const newDeps = action.dependencies;

      const addedSuccessors = newDeps.successors.filter(
        (id) => !oldDeps.successors.includes(id)
      );
      const removedSuccessors = oldDeps.successors.filter(
        (id) => !newDeps.successors.includes(id)
      );
      const addedPredecessors = newDeps.predecessors.filter(
        (id) => !oldDeps.predecessors.includes(id)
      );
      const removedPredecessors = oldDeps.predecessors.filter(
        (id) => !newDeps.predecessors.includes(id)
      );

      const syncedTemplates = syncInverseDependencies(
        patchedTemplates,
        action.templateId,
        addedSuccessors,
        removedSuccessors,
        addedPredecessors,
        removedPredecessors
      );

      return {
        ...state,
        draft: {
          ...state.draft,
          templates: syncedTemplates,
        },
        modifiedTemplateIds: nextModified,
      };
    }

    case 'reset_draft':
      return {
        ...state,
        initialDraft: action.draft,
        draft: action.draft,
        modifiedTemplateIds: new Set(),
      };

    case 'mark_saved':
      return {
        ...state,
        initialDraft: action.draft,
        draft: action.draft,
        modifiedTemplateIds: new Set(),
      };

    default:
      return state;
  }
}
